import { Pesée, Ticket } from '@/types/weighing';

export interface OfflineDataState {
  lastSync: Date | null;
  pendingChanges: number;
  syncStatus: 'idle' | 'syncing' | 'success' | 'error';
  isOfflineMode: boolean;
}

export interface SyncQueueItem {
  id: string;
  type: 'CREATE' | 'UPDATE' | 'DELETE';
  endpoint: string;
  data: any;
  timestamp: Date;
  retryCount: number;
  status: 'pending' | 'syncing' | 'completed' | 'failed';
}

export interface SyncResult {
  uploaded: number;
  downloaded: number;
  errors: string[];
  conflicts: number;
  duration: number;
}

export class OfflineStorageService {
  private dbName = 'PontBasculeOffline';
  private version = 1;
  private db: IDBDatabase | null = null;
  private isInitialized = false;

  async init(): Promise<void> {
    if (this.isInitialized) return;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      
      request.onerror = () => {
        console.error('Erreur ouverture IndexedDB:', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        resolve();
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Stores pour les données de référence
        if (!db.objectStoreNames.contains('camions')) {
          db.createObjectStore('camions', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('chauffeurs')) {
          db.createObjectStore('chauffeurs', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('produits')) {
          db.createObjectStore('produits', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('clients')) {
          db.createObjectStore('clients', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('fournisseurs')) {
          db.createObjectStore('fournisseurs', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('exportateurs')) {
          db.createObjectStore('exportateurs', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('transporteurs')) {
          db.createObjectStore('transporteurs', { keyPath: 'id' });
        }
        
        // Store pour les pesées avec index sur le statut et la date
        if (!db.objectStoreNames.contains('pesees')) {
          const peseesStore = db.createObjectStore('pesees', { keyPath: 'id' });
          peseesStore.createIndex('statut', 'statut', { unique: false });
          peseesStore.createIndex('date_heure', 'date_heure', { unique: false });
          peseesStore.createIndex('isOffline', 'isOffline', { unique: false });
        }
        
        // Store pour les tickets
        if (!db.objectStoreNames.contains('tickets')) {
          const ticketsStore = db.createObjectStore('tickets', { keyPath: 'id' });
          ticketsStore.createIndex('isOffline', 'isOffline', { unique: false });
        }
        
        // Store pour la queue de synchronisation
        if (!db.objectStoreNames.contains('sync_queue')) {
          const syncStore = db.createObjectStore('sync_queue', { keyPath: 'id', autoIncrement: true });
          syncStore.createIndex('type', 'type', { unique: false });
          syncStore.createIndex('status', 'status', { unique: false });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
        
        // Store pour les métadonnées de synchronisation
        if (!db.objectStoreNames.contains('sync_metadata')) {
          db.createObjectStore('sync_metadata', { keyPath: 'key' });
        }
      };
    });
  }



  // Méthodes génériques pour sauvegarder et récupérer des données
  async saveData(storeName: string, data: any): Promise<void> {
    if (!this.db) await this.init();
    
  
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      
      // Vérifier et générer une clé primaire si nécessaire
      const dataToSave = this.ensureValidKey(data, storeName);
      
  
      // Utiliser add si l'ID n'est pas valide, sinon put
      const request = (dataToSave.id && dataToSave.id !== undefined && dataToSave.id !== null) 
        ? store.put(dataToSave) 
        : store.add(dataToSave);
      
      request.onsuccess = () => {
       
        resolve();
      };
      
      request.onerror = () => {
        console.error(`❌ Erreur sauvegarde ${storeName}:`, {
          error: request.error,
          dataToSave,
          storeName,
          transactionState: transaction.error,
          operation: (dataToSave.id && dataToSave.id !== undefined && dataToSave.id !== null) ? 'put' : 'add'
        });
        reject(request.error);
      };
    });
  }

  /**
   * S'assure que les données ont une clé primaire valide
   */
  private ensureValidKey(data: any, storeName: string): any {
    // Si c'est un tableau, traiter chaque élément
    if (Array.isArray(data)) {
      return data.map(item => this.ensureValidKey(item, storeName));
    }
    
    // Si c'est un objet
    if (data && typeof data === 'object') {
      const dataCopy = { ...data };
      
      // Générer un ID si nécessaire
      if (!dataCopy.id) {
        dataCopy.id = this.generateId();
      }
      
      // Vérifier que l'ID n'est pas undefined ou null
      if (dataCopy.id === undefined || dataCopy.id === null) {
        dataCopy.id = this.generateId();
      }
      
      return dataCopy;
    }
    
    // Pour les autres types, créer un objet avec ID
    return {
      id: this.generateId(),
      value: data,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Génère un ID unique
   */
  private generateId(): string {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  
  /**
   * Sauvegarde une pesée avec gestion d'erreur spécifique
   */
  async saveOfflinePesee(pesee: any): Promise<void> {
    try {
      // S'assurer que la pesée a un ID
      if (!pesee.id) {
        pesee.id = this.generateId();
      }
      
      // Ajouter des métadonnées offline
      const peseeToSave = {
        ...pesee,
        isOffline: true,
        createdAt: pesee.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      await this.saveData('pesees', peseeToSave);
    
    } catch (error) {
      console.error('❌ Erreur sauvegarde pesée offline:', error);
      throw error;
    }
  }

  async getData(storeName: string, id?: string | number): Promise<any> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      
      let request: IDBRequest;
      
      if (id) {
        request = store.get(id);
      } else {
        request = store.getAll();
      }
      
      request.onsuccess = () => {
        resolve(request.result);
      };
      
      request.onerror = () => {
        console.error(`❌ Erreur récupération ${storeName}:`, request.error);
        reject(request.error);
      };
    });
  }

  async deleteData(storeName: string, id: string | number): Promise<void> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      
      const request = store.delete(id);
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = () => {
        console.error(`❌ Erreur suppression ${storeName}:`, request.error);
        reject(request.error);
      };
    });
  }

  // Méthodes spécifiques pour les pesées
  async savePesee(pesee: Pesée): Promise<void> {
    await this.saveData('pesees', pesee);
  }

  async getPesees(): Promise<Pesée[]> {
    return await this.getData('pesees');
  }

  async getPesee(id: string | number): Promise<Pesée | null> {
    return await this.getData('pesees', id);
  }

  async getOfflinePesees(): Promise<Pesée[]> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['pesees'], 'readonly');
      const store = transaction.objectStore('pesees');
      const index = store.index('isOffline');
      
      const request = index.getAll(1);
      
      request.onsuccess = () => {
        resolve(request.result);
      };
      
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  // Méthodes pour la queue de synchronisation
  async addToSyncQueue(item: Omit<SyncQueueItem, 'id'>): Promise<string> {
    if (!this.db) await this.init();
    
    const syncItem: SyncQueueItem = {
      ...item,
      id: `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      retryCount: 0,
      status: 'pending'
    };
    
    await this.saveData('sync_queue', syncItem);
    return syncItem.id;
  }

  async getSyncQueue(): Promise<SyncQueueItem[]> {
    return await this.getData('sync_queue');
  }

  async getPendingSyncItems(): Promise<SyncQueueItem[]> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['sync_queue'], 'readonly');
      const store = transaction.objectStore('sync_queue');
      const index = store.index('status');
      
      const request = index.getAll('pending');
      
      request.onsuccess = () => {
        resolve(request.result);
      };
      
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  async updateSyncItemStatus(id: string, status: SyncQueueItem['status']): Promise<void> {
    const item = await this.getData('sync_queue', id);
    if (item) {
      item.status = status;
      await this.saveData('sync_queue', item);
    }
  }

  async removeSyncItem(id: string): Promise<void> {
    await this.deleteData('sync_queue', id);
  }

  // Méthodes pour les métadonnées de synchronisation
  async setLastSyncDate(date: Date): Promise<void> {
    await this.saveData('sync_metadata', { key: 'lastSync', value: date.toISOString() });
  }

  async getLastSyncDate(): Promise<Date | null> {
    const metadata = await this.getData('sync_metadata', 'lastSync');
    return metadata ? new Date(metadata.value) : null;
  }

  async setOfflineMode(isOffline: boolean): Promise<void> {
    await this.saveData('sync_metadata', { key: 'offlineMode', value: isOffline });
  }

  async getOfflineMode(): Promise<boolean> {
    const metadata = await this.getData('sync_metadata', 'offlineMode');
    return metadata ? metadata.value : false;
  }

  // Méthodes utilitaires
  async getPendingChangesCount(): Promise<number> {
    const pendingItems = await this.getPendingSyncItems();
    return pendingItems.length;
  }

  async clearAllData(): Promise<void> {
    if (!this.db) await this.init();
    
    const storeNames = [
      'camions', 'chauffeurs', 'produits', 'clients', 'fournisseurs',
      'exportateurs', 'transporteurs', 'pesees', 'tickets', 'sync_queue'
    ];
    
    for (const storeName of storeNames) {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      await store.clear();
    }
    
  }

  async getStorageSize(): Promise<number> {
    if (!this.db) return 0;
    
    let totalSize = 0;
    const storeNames = Array.from(this.db.objectStoreNames);
    
    for (const storeName of storeNames) {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();
      
      await new Promise<void>((resolve) => {
        request.onsuccess = () => {
          const data = request.result;
          totalSize += JSON.stringify(data).length;
          resolve();
        };
      });
    }
    
    return totalSize;
  }

  // Méthodes pour les données de référence
  async saveReferenceData(type: string, data: any[]): Promise<void> {
    await this.saveData(type, data);
  }

  async getReferenceData(type: string): Promise<any[]> {
    return await this.getData(type) || [];
  }

  // Méthodes pour les tickets
  async saveTicket(ticket: Ticket): Promise<void> {
    await this.saveData('tickets', ticket);
  }

  async getTickets(): Promise<Ticket[]> {
    return await this.getData('tickets');
  }

  async getOfflineTickets(): Promise<Ticket[]> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['tickets'], 'readonly');
      const store = transaction.objectStore('tickets');
      const index = store.index('isOffline');
      
      const request = index.getAll(1);
      
      request.onsuccess = () => {
        resolve(request.result);
      };
      
      request.onerror = () => {
        reject(request.error);
      };
    });
  }
}

// Instance singleton
export const offlineStorage = new OfflineStorageService();
